import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useUser } from "@/client/services/user";

const API = "/api/integration/external-service/coding-platform/skill-tests";
const unwrap = (r: any) => r?.data?.result ?? r?.data;

const toArray = (v: any): any[] => (Array.isArray(v) ? v : []);

export function useSkillTests() {
  const { user } = useUser();
  const studentId = user?.id ?? "";

  const [tests, setTests] = useState<any[]>([]);
  const [facets, setFacets] = useState<any>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [filters, setFilters] = useState({ search: "", category: "", language: "", topic: "", difficulty: "" });
  const [page, setPage] = useState(0);

  const fetchTests = useCallback(async (f = filters, p = 0) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        studentId,
        page: p + 1,
        limit: 12,
      };
      if (f.search) params.search = f.search;
      if (f.category) params.category = f.category;
      if (f.language) params.programmingLanguage = f.language;
      if (f.topic) params.topic = f.topic;
      if (f.difficulty) params.difficulty = f.difficulty;

      const res = await axios.get(API, { params });
      const d = unwrap(res);
      setTests(toArray(d?.data ?? d));
      setTotal(typeof d?.total === "number" ? d.total : 0);
      setFacets(d?.facets ?? {});
    } finally { setLoading(false); }
  }, [studentId]);

  const fetchHistory = useCallback(async () => {
    if (!studentId) return;
    const res = await axios.get(`${API}/history/${studentId}`);
    setHistory(toArray(unwrap(res)));
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    fetchTests(); fetchHistory();
  }, [studentId]);

  const applyFilters = (f: typeof filters) => { setFilters(f); setPage(0); fetchTests(f, 0); };
  const changePage = (p: number) => { setPage(p); fetchTests(filters, p); };

  return { studentId, tests, facets, total, loading, history, filters, applyFilters, page, changePage, refetch: () => { fetchTests(filters, page); fetchHistory(); } };
}

export async function startTestAttempt(studentId: string, testId: string, student?: {
  name?: string;
  email?: string;
  organizationName?: string;
  organizationId?: string;
}) {
  const res = await axios.post(`${API}/start`, {
    studentId,
    testId,
    studentName: student?.name ?? "",
    studentEmail: student?.email ?? "",
    organizationName: student?.organizationName ?? "",
    organizationId: student?.organizationId ?? "",
  });
  return res?.data?.result ?? res?.data;
}

export async function saveAnswer(payload: any) {
  const res = await axios.post(`${API}/answer`, payload);
  return res?.data?.result ?? res?.data;
}

export async function submitTest(studentId: string, attemptId: string, timeTakenSeconds: number) {
  const res = await axios.post(`${API}/submit`, { studentId, attemptId, timeTakenSeconds });
  return res?.data?.result ?? res?.data;
}

export async function getAttemptResult(attemptId: string) {
  const res = await axios.get(`${API}/attempt/${attemptId}/result`);
  return res?.data?.result ?? res?.data;
}

export async function fetchTestSubmissions(studentId: string, testId: string) {
  const res = await axios.get(`${API}/${testId}/submissions/${studentId}`);
  const payload = unwrap(res);
  const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return { test: payload?.test, data, total: payload?.total ?? data.length };
}
